import type { GmvBreakdownEntity, GmvBreakdownLens } from "@unbox/api";
import { ArrowRight, Stars01 } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { pluralize } from "@/utils/text";
import type { AskRequest } from "../lib/ask-prompts";
import {
  ASK_ACTION,
  askAboutQuestion,
  BEST_VIDEO_PHRASE,
  CHECK_ALL_ACTION,
  MEMBERS_NOTE,
  NEW_VIDEOS_PHRASE,
  OPEN_ACTION,
  REMAINDER_CHIP,
  REMAINDER_COUNTS,
  REMAINDER_ROLE,
  ROLE_LABEL,
} from "../lib/gmv-breakdown-copy";

export type PanelEntity = GmvBreakdownEntity & {
  isRemainder: boolean;
  members?: GmvBreakdownEntity[];
  memberCount?: number;
};

type GmvBreakdownPanelProps = {
  entity: PanelEntity;
  lens: GmvBreakdownLens;
  phrase: string;
  lensTotal: number;
  formatGmv: (value: number) => string;
  onAsk: (request: AskRequest) => void;
  onOpen: (entity: PanelEntity) => void;
  onOpenAll: () => void;
};

const INK = "#231539";
const UP = "#3FA971";
const DOWN = "#E0483F";

const COUNT_UNIT_LABELS: Record<NonNullable<GmvBreakdownEntity["countUnit"]>, string> = {
  unitSales: "units sold",
  videos: "videos",
  creators: "creators",
};

const NAMED_MEMBERS = 4;

const initials = (name: string) =>
  name
    .replace(/[^\p{L}\p{N} ]/gu, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("") || "•";

function PanelSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 border-t pt-4" style={{ borderColor: `${INK}14` }}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: `${INK}80` }}>
        {title}
      </p>
      {children}
    </div>
  );
}

function Delta({ value, formatGmv }: { value: number; formatGmv: (value: number) => string }) {
  return (
    <span
      className="shrink-0 text-[14px] leading-5 font-semibold tabular-nums"
      style={{ color: value < 0 ? DOWN : UP }}
    >
      {value < 0 ? "−" : "+"}
      {formatGmv(Math.abs(value))}
    </span>
  );
}

function SummaryText({
  entity,
  lens,
  phrase,
  share,
  delta,
  formatGmv,
}: {
  entity: PanelEntity;
  lens: GmvBreakdownLens;
  phrase: string;
  share: number;
  delta: number;
  formatGmv: (value: number) => string;
}) {
  if (entity.isRemainder) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-[15px] leading-6" style={{ color: INK }}>
          Earned <strong className="font-semibold">{formatGmv(entity.gmv)}</strong>
          <br />
          <strong className="font-semibold">{share}%</strong> of video GMV
        </p>
        <PeriodChange delta={delta} phrase={phrase} formatGmv={formatGmv} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[15px] leading-6" style={{ color: INK }}>
        {entity.lifetimeGmv != null && (
          <>
            Earned <strong className="font-semibold">{formatGmv(entity.lifetimeGmv)}</strong>
          </>
        )}
        {entity.lifetimeVideoCount != null && (
          <>
            <br />
            From <strong className="font-semibold">{pluralize(entity.lifetimeVideoCount, "video", "videos")}</strong>
          </>
        )}
        {entity.bestVideoGmv != null && (
          <>
            <br />
            {BEST_VIDEO_PHRASE[lens].before} <strong className="font-semibold">{formatGmv(entity.bestVideoGmv)}</strong>
            {BEST_VIDEO_PHRASE[lens].after}
          </>
        )}
        {entity.topCreatorName != null && entity.topCreatorSharePct != null && (
          <>
            <br />
            <strong className="font-semibold">{entity.topCreatorName}</strong> made {entity.topCreatorSharePct}% of GMV
          </>
        )}
      </p>
      <PeriodChange delta={delta} phrase={phrase} formatGmv={formatGmv}>
        {entity.periodNewVideoCount != null &&
          (() => {
            const newVideosPhrase = NEW_VIDEOS_PHRASE[lens](entity.periodNewVideoCount);
            return (
              newVideosPhrase && (
                <>
                  <br />
                  {newVideosPhrase}
                </>
              )
            );
          })()}
      </PeriodChange>
    </div>
  );
}

function PeriodChange({
  delta,
  phrase,
  formatGmv,
  children,
}: {
  delta: number;
  phrase: string;
  formatGmv: (value: number) => string;
  children?: ReactNode;
}) {
  return (
    <p className="text-[15px] leading-6" style={{ color: INK }}>
      {delta >= 0 ? "GMV up " : "GMV down "}
      <strong className="font-semibold" style={{ color: delta >= 0 ? UP : DOWN }}>
        {formatGmv(Math.abs(delta))}
      </strong>
      {children}
      <br />
      in the {phrase}.
    </p>
  );
}

export function GmvBreakdownPanel({
  entity,
  lens,
  phrase,
  lensTotal,
  formatGmv,
  onAsk,
  onOpen,
  onOpenAll,
}: GmvBreakdownPanelProps) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  const role = entity.isRemainder ? REMAINDER_ROLE[lens] : ROLE_LABEL[lens];
  const subtitle = entity.isRemainder ? REMAINDER_COUNTS[lens] : entity.subtitle;
  const delta = entity.gmv - entity.priorGmv;
  const share = lensTotal > 0 ? Math.round((entity.gmv / lensTotal) * 100) : 0;
  const named = entity.members?.slice(0, NAMED_MEMBERS) ?? [];
  const unnamed = Math.max(0, (entity.memberCount ?? 0) - named.length);
  const unnamedDelta = delta - named.reduce((total, member) => total + (member.gmv - member.priorGmv), 0);

  return (
    <aside
      className="panel-swap flex w-full shrink-0 flex-col border-t pt-5 lg:w-[430px] lg:border-t-0 lg:border-l lg:pt-0 lg:pl-7"
      style={{ borderColor: `${INK}14` }}
    >
      <div className="panel-content-in flex flex-1 flex-col gap-5">
        <div className="flex flex-col gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: `${INK}80` }}>
            {role}
          </p>

          <div className="flex items-start gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <h3
                title={entity.name}
                style={{ color: INK, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                className="overflow-hidden text-lg leading-6 font-semibold"
              >
                {entity.name}
              </h3>
              {subtitle && (
                <p className="text-[13px] leading-5" style={{ color: `${INK}a6` }}>
                  {subtitle}
                </p>
              )}
              {entity.isRemainder ? (
                <BadgeWithDot size="sm" color="gray" className="mt-1">
                  {REMAINDER_CHIP}
                </BadgeWithDot>
              ) : (
                entity.count != null &&
                entity.countUnit && (
                  <BadgeWithDot size="sm" color="brand" className="mt-1">
                    {entity.count.toLocaleString()} {COUNT_UNIT_LABELS[entity.countUnit]}
                  </BadgeWithDot>
                )
              )}
            </div>

            {!entity.isRemainder &&
              lens !== "cohort" &&
              (entity.thumbnail && !thumbnailFailed ? (
                <img
                  src={entity.thumbnail}
                  alt=""
                  onError={() => setThumbnailFailed(true)}
                  referrerPolicy="no-referrer"
                  className="size-20 shrink-0 rounded-[6px] object-cover"
                />
              ) : (
                <span
                  aria-hidden="true"
                  style={{ backgroundColor: `${INK}0f`, color: `${INK}80` }}
                  className="flex size-20 shrink-0 items-center justify-center rounded-[6px] text-lg font-semibold"
                >
                  {initials(entity.name)}
                </span>
              ))}
          </div>
        </div>

        <SummaryText entity={entity} lens={lens} phrase={phrase} share={share} delta={delta} formatGmv={formatGmv} />

        {entity.isRemainder && named.length > 0 && (
          <PanelSection title="What's in it">
            <p className="text-[13px] leading-5" style={{ color: `${INK}8c` }}>
              {MEMBERS_NOTE}
            </p>
            <ul className="m-0 flex list-none flex-col p-0">
              {named.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center justify-between gap-4 border-b py-1.5 last:border-b-0"
                  style={{ borderColor: `${INK}0f` }}
                >
                  <span className="min-w-0 truncate text-[14px] leading-5" style={{ color: INK }}>
                    {member.name}
                  </span>
                  <Delta value={member.gmv - member.priorGmv} formatGmv={formatGmv} />
                </li>
              ))}
            </ul>
            {unnamed > 0 && (
              <p
                className="flex items-center justify-between gap-4 py-1.5 text-[13px] leading-5"
                style={{ color: `${INK}73` }}
              >
                <span>…and {unnamed.toLocaleString()} more</span>
                <Delta value={unnamedDelta} formatGmv={formatGmv} />
              </p>
            )}
            <button
              type="button"
              onClick={onOpenAll}
              className="mt-1 inline-flex w-fit cursor-pointer items-center gap-1.5 text-[14px] leading-5 font-semibold text-brand-600 transition-colors duration-150 hover:text-brand-700"
            >
              {CHECK_ALL_ACTION[lens]}
              <ArrowRight width={15} height={15} aria-hidden="true" className="shrink-0" />
            </button>
          </PanelSection>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-3 pt-2">
          {!entity.isRemainder && (
            <button
              type="button"
              onClick={() => onOpen(entity)}
              style={{ backgroundColor: INK }}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-opacity duration-150 hover:opacity-90"
            >
              {OPEN_ACTION[lens]}
              <ArrowRight width={15} height={15} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onAsk({ question: askAboutQuestion(entity.name, role) })}
            className="inline-flex cursor-pointer items-center gap-1.5 px-1 py-1 text-sm font-medium text-brand-600 transition-colors duration-150 hover:text-brand-700"
          >
            <Stars01 width={14} height={14} aria-hidden="true" className="ask-sparkle" />
            {ASK_ACTION[lens]}
          </button>
        </div>
      </div>
    </aside>
  );
}
